import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { showToast } from '../../utils/toast';
import './CircleEventDetail.css';

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
}

const STATUS_LABELS = { recruiting: '参加募集中', closed: '受付終了', cancelled: 'キャンセル' };

function CircleEventDetail() {
  const { id: circleId, eventId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [circle, setCircle] = useState(null);
  const [myParticipation, setMyParticipation] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadData = () => {
    setLoading(true);
    setError('');
    Promise.all([
      getDoc(doc(db, 'recruitments', circleId, 'events', eventId)),
      getDoc(doc(db, 'recruitments', circleId)),
      currentUser ? getDoc(doc(db, 'recruitments', circleId, 'events', eventId, 'participants', currentUser.uid)) : Promise.resolve(null),
      getDocs(collection(db, 'recruitments', circleId, 'events', eventId, 'participants')),
    ]).then(([evSnap, circleSnap, mySnap, partSnap]) => {
      if (!evSnap.exists()) { setError('イベントが見つかりませんでした。'); return; }
      setEvent({ id: evSnap.id, ...evSnap.data() });
      if (circleSnap.exists()) setCircle({ id: circleSnap.id, ...circleSnap.data() });
      setMyParticipation(mySnap?.exists() ? { id: mySnap.id, ...mySnap.data() } : null);
      setParticipants(partSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch(() => setError('データの取得に失敗しました。'))
      .finally(() => setLoading(false));
  };

  useEffect(loadData, [circleId, eventId, currentUser]);

  const isOrganizer = currentUser && circle?.userId === currentUser.uid;
  const confirmed = participants.filter((p) => p.status === 'confirmed');
  const waitlist = participants.filter((p) => p.status === 'waitlist');
  const isFull = event?.capacity > 0 && confirmed.length >= event.capacity;

  const handleJoin = async () => {
    if (!currentUser) { navigate('/login'); return; }
    setJoining(true);
    try {
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const profile = userSnap.exists() ? userSnap.data() : {};

      await runTransaction(db, async (tx) => {
        const evRef = doc(db, 'recruitments', circleId, 'events', eventId);
        const partRef = doc(db, 'recruitments', circleId, 'events', eventId, 'participants', currentUser.uid);
        const evSnap = await tx.get(evRef);
        if (!evSnap.exists()) throw new Error('Event not found');
        const ev = evSnap.data();
        const currentConfirmed = ev.participantCount || 0;
        const isEventFull = ev.capacity > 0 && currentConfirmed >= ev.capacity;
        const status = isEventFull ? 'waitlist' : 'confirmed';

        tx.set(partRef, {
          status,
          registeredAt: serverTimestamp(),
          nickname: profile.nickname || currentUser.displayName || '',
          photoURL: profile.photoURL || currentUser.photoURL || '',
        });
        if (status === 'confirmed') {
          tx.update(evRef, { participantCount: currentConfirmed + 1 });
        }
      });

      showToast({
        message: isFull ? 'キャンセル待ちに追加されました。' : '参加登録しました。',
        type: 'success',
      });
      loadData();
    } catch (err) {
      console.error('Event join failed:', err);
      showToast({ message: '参加登録に失敗しました。', type: 'error' });
    } finally {
      setJoining(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('参加をキャンセルしますか？')) return;
    setCancelling(true);
    try {
      await runTransaction(db, async (tx) => {
        const evRef = doc(db, 'recruitments', circleId, 'events', eventId);
        const partRef = doc(db, 'recruitments', circleId, 'events', eventId, 'participants', currentUser.uid);
        const [evSnap, partSnap] = await Promise.all([tx.get(evRef), tx.get(partRef)]);
        if (!evSnap.exists() || !partSnap.exists()) throw new Error('Not found');

        const wasConfirmed = partSnap.data().status === 'confirmed';
        tx.update(partRef, { status: 'cancelled', cancelledAt: serverTimestamp() });

        if (wasConfirmed) {
          const currentCount = evSnap.data().participantCount || 0;
          tx.update(evRef, { participantCount: Math.max(0, currentCount - 1) });

          // キャンセル待ちから繰り上げ
          const waitSnap = await getDocs(collection(db, 'recruitments', circleId, 'events', eventId, 'participants'));
          const firstWait = waitSnap.docs.find((d) => d.data().status === 'waitlist');
          if (firstWait) {
            tx.update(firstWait.ref, { status: 'confirmed', promotedAt: serverTimestamp() });
            tx.update(evRef, { participantCount: Math.max(0, currentCount - 1) + 1 });
          }
        }
      });

      showToast({ message: '参加をキャンセルしました。', type: 'success' });
      loadData();
    } catch (err) {
      console.error('Event cancel failed:', err);
      showToast({ message: 'キャンセルに失敗しました。', type: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await updateDoc(doc(db, 'recruitments', circleId, 'events', eventId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      setEvent((prev) => ({ ...prev, status: newStatus }));
      showToast({ message: `ステータスを「${STATUS_LABELS[newStatus]}」に変更しました。`, type: 'success' });
    } catch (err) {
      console.error('Event status change failed:', err);
      showToast({ message: 'ステータスの変更に失敗しました。', type: 'error' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('このイベントを削除しますか？この操作は元に戻せません。')) return;
    setDeleting(true);
    try {
      await updateDoc(doc(db, 'recruitments', circleId, 'events', eventId), {
        status: 'deleted',
        deletedAt: serverTimestamp(),
      });
      showToast({ message: 'イベントを削除しました。', type: 'success' });
      navigate(`/circles/${circleId}/manage`);
    } catch (err) {
      console.error('Event delete failed:', err);
      showToast({ message: '削除に失敗しました。', type: 'error' });
      setDeleting(false);
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;

  if (error) {
    return (
      <div className="ced-page">
        <div className="ced-error">
          <p>{error}</p>
          <Link to={`/circles/${circleId}/manage`} className="ced-btn ced-btn--ghost">管理画面へ戻る</Link>
        </div>
      </div>
    );
  }

  const myStatus = myParticipation?.status;
  const isActive = myStatus && myStatus !== 'cancelled';

  return (
    <div className="ced-page">
      <article className="ced-article">
        <header className="ced-header">
          {circle && (
            <Link to={`/circles/${circleId}/manage`} className="ced-circle-link">{circle.groupName}</Link>
          )}
          <div className="ced-title-row">
            <h1>{event.title || event.mountain}</h1>
            <span className={`ced-status ced-status--${event.status}`}>
              {STATUS_LABELS[event.status] || event.status}
            </span>
          </div>
        </header>

        <div className="ced-info-grid">
          <div className="ced-info-item">
            <span>開催日</span>
            <strong>{formatDate(event.date)}</strong>
          </div>
          <div className="ced-info-item">
            <span>山名</span>
            <strong>{event.mountain}</strong>
          </div>
          <div className="ced-info-item">
            <span>集合場所</span>
            <strong>{event.meetingPoint}</strong>
          </div>
          <div className="ced-info-item">
            <span>定員</span>
            <strong>{event.capacity > 0 ? `${event.capacity}人` : '無制限'}</strong>
          </div>
          <div className="ced-info-item">
            <span>参加状況</span>
            <strong>
              {confirmed.length}人参加
              {waitlist.length > 0 && ` / ${waitlist.length}人キャンセル待ち`}
            </strong>
          </div>
          {event.fee > 0 && (
            <div className="ced-info-item">
              <span>参加費</span>
              <strong>{event.fee.toLocaleString()}円</strong>
            </div>
          )}
          {event.course && (
            <div className="ced-info-item">
              <span>コース</span>
              <strong>{event.course}</strong>
            </div>
          )}
          {event.requiredGear && (
            <div className="ced-info-item ced-info-item--full">
              <span>必要装備</span>
              <strong>{event.requiredGear}</strong>
            </div>
          )}
        </div>

        {event.description && (
          <div className="ced-description">
            {event.description.split('\n').map((line, i) => (
              line ? <p key={i}>{line}</p> : <br key={i} />
            ))}
          </div>
        )}

        {/* 参加アクション */}
        {!isOrganizer && event.status === 'recruiting' && (
          <div className="ced-join-section">
            {!isActive ? (
              <button
                type="button"
                className={`ced-btn ced-btn--primary${isFull ? ' ced-btn--waitlist' : ''}`}
                onClick={handleJoin}
                disabled={joining}
              >
                {joining ? '処理中...' : isFull ? 'キャンセル待ちで登録' : '参加する'}
              </button>
            ) : (
              <div className="ced-my-status">
                <span className={`ced-my-badge ced-my-badge--${myStatus}`}>
                  {myStatus === 'confirmed' ? '参加確定' : 'キャンセル待ち'}
                </span>
                <button
                  type="button"
                  className="ced-btn ced-btn--ghost"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? '処理中...' : 'キャンセルする'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* 参加者一覧（オーナーのみ） */}
        {isOrganizer && confirmed.length > 0 && (
          <div className="ced-participants">
            <h2>参加者 ({confirmed.length}人)</h2>
            <ul className="ced-participant-list">
              {confirmed.map((p) => (
                <li key={p.id}>
                  {p.photoURL && <img src={p.photoURL} alt={p.nickname} />}
                  <span>{p.nickname || p.id}</span>
                </li>
              ))}
            </ul>
            {waitlist.length > 0 && (
              <>
                <h2>キャンセル待ち ({waitlist.length}人)</h2>
                <ul className="ced-participant-list">
                  {waitlist.map((p) => (
                    <li key={p.id}>
                      {p.photoURL && <img src={p.photoURL} alt={p.nickname} />}
                      <span>{p.nickname || p.id}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {/* 主催者管理アクション */}
        {isOrganizer && (
          <div className="ced-owner-actions">
            <Link to={`/circles/${circleId}/events/${eventId}/edit`} className="ced-btn ced-btn--ghost">
              編集する
            </Link>
            {event.status === 'recruiting' && (
              <button
                type="button"
                className="ced-btn ced-btn--ghost"
                onClick={() => handleStatusChange('closed')}
                disabled={updatingStatus}
              >
                受付を終了する
              </button>
            )}
            {event.status === 'closed' && (
              <button
                type="button"
                className="ced-btn ced-btn--ghost"
                onClick={() => handleStatusChange('recruiting')}
                disabled={updatingStatus}
              >
                受付を再開する
              </button>
            )}
            <button
              type="button"
              className="ced-btn ced-btn--danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? '削除中...' : 'イベントを削除'}
            </button>
          </div>
        )}
      </article>
    </div>
  );
}

export default CircleEventDetail;
